import React, {useContext, useEffect, useMemo, useState} from 'react';
import {flatten, uniq} from "lodash";
import {SchemaForm} from "../../../components/forms";
import {organisations, recordings} from "../../../services/api";
import {errorToast, successToast} from "../../../utils/toaster";
import {DataPageContext} from "../context";
import {ScheduledReports} from "./scheduled-reports";


const _orgSchema = {
    type: "object",
    required: [
        "id",
        "name",
    ],
    properties: {
        id: {
            type: "string",
            pattern: "^[a-f\\d]{23,26}$",
            readOnly: true
        },
        name: {
            type: "string"
        },
        createdAt: {
            type: "string",
            readOnly: true
        },
        updatedAt: {
            type: "string",
            readOnly: true
        },
        theme: {
            type: ["string", "null"],
            pattern: "^[a-f\\d]{23,26}$",
        },
        options: {
            type: "object",
            required: ["startOfWeek"],
            properties: {
                startOfWeek: { type: "string", enum: ["sunday", "monday"] },
                showSettingUpLocations: { type: "boolean", default: false },
                disableAgeAndGenderOnFrontEnd: { type: "boolean", default: false },
                ignoreLingerUnderOverride: { type: "boolean", default: false },
                isDemoUser: { type: "boolean", default: false },
                orgHasStaffDetection: { type: "boolean", default: false },
                showDemoLivePage: { type: "boolean", default: false },
                showHeadcountPlanner: { type: "boolean", default: false },
                showLivePage: { type: "boolean", default: false },
                hasInteractionHeatmaps: { type: "boolean", default: false },
                useCaptureRateV2: { type: "boolean", default: false },
            }
        }
    }
};

const _uiInfoSchema = {
    type: "Group",
    label: "Configuration",
    elements: [
        {
            type: "HorizontalLayout",
            elements: [
                {type: "Control", scope: "#/properties/name"},
                {type: "Control", scope: "#/properties/id", options: {disabled: true}},
                {type: "Control", scope: "#/properties/createdAt", options: {disabled: true}},
                {type: "Control", scope: "#/properties/updatedAt", options: {disabled: true}},
            ]
        }
    ]
};

const _uiOptionsSchema = {
    type: "Group",
    label: "Options",
    options: {collapsed: true},
    elements: [
        {type: "Control", scope: "#/properties/options/properties/startOfWeek"},
        ...Object.keys(
            _orgSchema.properties.options.properties
        ).filter(key => (
            _orgSchema.properties.options.properties[key].type === "boolean"
        )).map(key => ({
            type: "Control",
            scope: "#/properties/options/properties/" + key,
            options: {toggle: true}
        }))
    ]
};


const METRIC_KEYS_BY_SECTION = {
    movement: [
        "entries", "passers_by", "capture_rate", "average_dwell_time", "average_occupancy"
    ],
    movementWithFilters: [
        "internal_line_crossings", "internal_entries", "internal_passers_by",
        "internal_capture_rate", "internal_line_crossings_per_location_entrant"
    ],
    zones: [
        "area_entries", "area_total_dwell_time", "area_average_dwell_time",
        "area_average_occupancy", "area_max_occupancy", "area_utilisation",
        "area_entries_per_location_entrant", "area_dwell_per_location_entrant"
    ],
    serviceAreas: [
        "area_entries", "area_total_dwell_time", "area_average_dwell_time",
        "area_average_occupancy", "area_max_occupancy", "area_utilisation",
        "area_entries_per_location_entrant", "area_dwell_per_location_entrant"
    ],
    sales: [
        "sales_transaction_count", "sales_volume", "sales_atv",
        "sales_units", "sales_upt", "sales_conversion_rate"
    ],
};

const _expandTaxonomyLevels = (taxonomies) => {
    const levels = new Set();
    for (const tax of taxonomies) {
        const parts = tax.split(':');
        for (let i = 1; i <= parts.length; i++) {
            levels.add(parts.slice(0, i).join(':'));
        }
    }
    return [...levels].sort();
};

const _isEmpty = (v) => v === null || v === '' || (Array.isArray(v) && v.length === 0);

const _sanitize = (schema, data) => {
    if (data == null || typeof data !== 'object' || Array.isArray(data)) return data;
    const result = {};
    for (const [key, value] of Object.entries(data)) {
        if (_isEmpty(value)) continue;
        const fieldSchema = schema?.properties?.[key] || schema?.additionalProperties;
        result[key] = typeof value === 'object' ? _sanitize(fieldSchema, value) : value;
    }
    return result;
};

const _stripEndpoints = (config) => {
    const result = {};
    for (const [key, section] of Object.entries(config)) {
        if (!section) continue;
        const {metrics, ...rest} = section;
        result[key] = {
            ...rest,
            metrics: metrics?.map(({endpoint, ...m}) => m)
        };
    }
    return result;
};

const _buildMetricsSchema = (taxonomies, keysAndValues) => {
    const allMetricKeys = [...new Set(Object.values(METRIC_KEYS_BY_SECTION).flat())].sort();
    return {
        type: "object",
        minProperties: 1,
        errorMessage: {minProperties: "Add at least one metric section"},
        propertyNames: { enum: Object.keys(METRIC_KEYS_BY_SECTION) },
        additionalProperties: {
            type: "object",
            required: ["title", "description", "metrics"],
            errorMessage: {
                required: {
                    title: "Section title is required",
                    description: "Section description is required",
                    metrics: "Add at least one metric",
                }
            },
            properties: {
                title: {type: "string", minLength: 1, errorMessage: {minLength: "Title cannot be empty"}},
                description: {type: "string"},
                 metrics: {
                    type: "array",
                    minItems: 1,
                    errorMessage: {minItems: "Add at least one metric"},
                    items: {
                        type: "object",
                        required: ["key", "label", "description"],
                        errorMessage: {
                            required: {
                                key: "Metric key is required",
                                label: "Metric label is required",
                                description: "Metric description is required",
                            }
                        },
                        properties: {
                            showOnStartup: {type: "boolean"},
                            key: {type: "string", enum: allMetricKeys, errorMessage: {enum: "Pick a valid metric key"}},
                            label: {type: "string", minLength: 1, maxLength: 50, errorMessage: {minLength: "Label cannot be empty", maxLength: "Label must be 50 characters or fewer"}},
                            description: {type: "string", minLength:1, maxLength: 150, errorMessage: {minLength: "Description cannot be empty", maxLength: "Description must be 150 characters or fewer"}},
                        }
                    }
                }
            }
        },
        dependentSchemas: Object.fromEntries(
            Object.entries(METRIC_KEYS_BY_SECTION).map(([section, keys]) => {
                const taxonomyProps = (section === 'zones' || section === 'serviceAreas') ? {
                    taxonomyBlockFilter: {type: ["string", "null"], ...(taxonomies?.length ? {examples: taxonomies} : {})},
                    taxonomyAllowFilter: {type: ["string", "null"], ...(taxonomies?.length ? {examples: taxonomies} : {})},
                    taxonomySelectLabel: {type: ["string", "null"]},
                    showOnStartupTaxonomies: {
                        type: ["array", "null"],
                        items: {type: "string", ...(taxonomies?.length ? {examples: taxonomies} : {})}
                    },
                } : {};
                const filterProps = (section === 'movementWithFilters') ? {
                    showOnStartupFilters: {
                        type: ["array", "null"],
                        items: {
                            type: "object",
                            required: ["key", "values"],
                            errorMessage: {
                                required: {
                                    key: "Filter key is required",
                                    values: "Filter values are required",
                                }
                            },
                            properties: {
                                key: {type: "string", ...(keysAndValues?.keys?.length ? {examples: keysAndValues.keys} : {})},
                                values: {type: "array", items: {type: "string", ...(keysAndValues?.values?.length ? {examples: keysAndValues.values} : {})}}
                            },
                            additionalProperties: false
                        }
                    },
                } : {};
                return [section, { properties: { [section]: { properties: {
                    ...taxonomyProps,
                    ...filterProps,
                    metrics: { items: { properties: {
                        key: { enum: keys, errorMessage: {enum: "Invalid metric key for this section"} }
                    }}}
                }}}}];
            })
        )
    };
};


const _uiMetricsSchema = {
    type: "Control",
    label: "Available Metrics",
    scope: "#",
    options: {
        collapsed: true,
        itemUISchema: {
            type: "Control",
            options: {
                editMode: 'drawer',
                itemTitle: 'title',
                itemDescription: 'description',
                itemUISchema: {
                    type: 'VerticalLayout',
                    elements: [
                        {
                            type: 'Control',
                            scope: '#/properties/metrics',
                            options: {
                                itemUISchema: {
                                    type: 'Control',
                                    options: {
                                        itemTitle: 'label',
                                        itemDescription: 'description',
                                        itemUISchema: {
                                            type: "HorizontalLayout",
                                            options: {wrap: false},
                                            elements: [
                                                {type: "Control", scope: "#/properties/key", options: {fill: true, maxWidth: '250px'}},
                                                {type: "Control", scope: "#/properties/showOnStartup", options: {toggle: true, maxWidth: '180px'}},
                                            ]
                                        }
                                    }
                                }
                            }
                        },
                        {
                            type: "Group",
                            label: "Advanced",
                            options: {collapsed: true},
                            elements: [
                                {
                                    type: "HorizontalLayout",
                                    elements: [
                                        {type: "Control", scope: "#/properties/taxonomyBlockFilter", options: {fill: true}},
                                        {type: "Control", scope: "#/properties/taxonomyAllowFilter", options: {fill: true}},
                                        {type: "Control", scope: "#/properties/taxonomySelectLabel", options: {fill: true}},
                                    ]
                                },
                                {
                                    type: "VerticalLayout",
                                    elements: [
                                        {type: "Control", scope: "#/properties/showOnStartupTaxonomies", options: {fill: true}},
                                        {
                                            type: "Control",
                                            scope: "#/properties/showOnStartupFilters",
                                            options: {
                                                fill: true,
                                                horizontal: true,
                                                itemUISchema: {
                                                    type: "Control",
                                                    options: {wrap: true, horizontal: true}
                                                }
                                            }
                                        },
                                    ]
                                },
                            ]
                        }
                    ]
                }
            }
        }
    }
};


const OneOrg = ({orgid}) => {
    const {setLoading, setNav, setControls} = useContext(DataPageContext);
    const [info, setInfo] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editErrors, setEditErrors] = useState(false);
    const [metricsData, setMetricsData] = useState(null);
    const [metricsErrors, setMetricsErrors] = useState(false);
    const [optionsDirty, setOptionsDirty] = useState(false);
    const [metricsDirty, setMetricsDirty] = useState(false);
    const [taxonomiesInUse, setTaxonomiesInUse] = useState(null);
    const [keysAndValuesInUse, setKeysAndValuesInUse] = useState(null);

    const metricsSchema = useMemo(
        () => _buildMetricsSchema(taxonomiesInUse, keysAndValuesInUse),
        [taxonomiesInUse, keysAndValuesInUse]
    );

    // editing toggle — update controls and handle save
    useEffect(() => {
        setControls([
            {name: editing ? "Save" : "Edit", onClick: () => setEditing(prev => !prev), icon: editing ? "floppy-disk" : "edit", confirm: editing}
        ]);

        if (!editing && info) {
            if (editErrors || metricsErrors) {
                errorToast({message: "Some fields are wrong", timeout: 5000});
                setEditing(true);
                return;
            }

            const saves = [];
            if (optionsDirty) {
                saves.push(
                    organisations.update(orgid, {options: info.options})
                        .then((updatedInfo) => { setInfo(updatedInfo); successToast({message: "Updated " + updatedInfo.name}); })
                        .catch((err) => { console.log(err); errorToast({message: "Failed to save org: " + err.message, timeout: 5000}); throw err; })
                );
            }
            if (metricsDirty) {
                saves.push(
                    organisations.availableMetrics.update(orgid, _sanitize(metricsSchema, metricsData))
                        .then((resp) => { setMetricsData(_stripEndpoints(resp.config)); successToast({message: "Updated available metrics"}); })
                        .catch((err) => { console.log(err); errorToast({message: "Failed to save metrics: " + err.message, timeout: 5000}); throw err; })
                );
            }

            if (saves.length) {
                setLoading({status: true, location: "Saving..."});
                Promise.allSettled(saves).then((results) => {
                    setOptionsDirty(false);
                    setMetricsDirty(false);
                    if (results.some(r => r.status === 'rejected')) setEditing(true);
                    setLoading({status: false, location: info.name + " (" + orgid + ")"});
                });
            }
        }
        return () => setControls([]);
    }, [editing]);

    // load organisation data
    useEffect(() => {
        console.log("Loading orgid: " + orgid);
        setLoading({status: true, location: "Loading organisation: " + orgid});

        organisations.get(orgid).then(loadedInfo => {
            console.log("Loaded orgid: " + orgid);
            setInfo(loadedInfo);
            setNav({
                right: [
                    {name: "Locations", path: "/locs?org=" + orgid, icon: "area-of-interest"},
                    {name: "Devices", path: "/devs?org=" + orgid, icon: "desktop"},
                    {name: "Cameras", path: "/recs?org=" + orgid, icon: "camera"},
                    {name: "Users", path: "/orgs/" + orgid + "/users", icon: "people"},
                ]
            });
            setLoading({status: false, location: loadedInfo.name + " (" + loadedInfo.id + ")" + " ... loading extra resources"});

            // fetch extra resources (taxonomies, keys and values from lineContexts)
            recordings.list({
                include: 'areaContexts,lineContexts', withinOrganisations: orgid
            }).then(recs => {
                const allAreaContexts = flatten(recs.map((r) => r.areaContexts)).filter(Boolean);
                const allLineContexts = flatten(recs.map((r) => r.lineContexts)).filter(Boolean);
                setTaxonomiesInUse(_expandTaxonomyLevels(uniq(allAreaContexts.map((ac) => ac.taxonomy).filter(Boolean))));
                const kv = allLineContexts.filter(
                    (lc) => lc.type === 'within-location-entry' || lc.type === 'within-location-pass-by'
                ).reduce((acc, lc) => {
                    lc.metadata.forEach(({key, value}) => {
                        acc.keys = uniq([...acc.keys, key]);
                        acc.values = uniq([...acc.values, value]);
                    });
                    return acc;
                }, {keys: [], values: []});
                setKeysAndValuesInUse({keys: kv.keys.sort(), values: kv.values.sort()});
                setLoading({status: false, location: loadedInfo.name + " (" + loadedInfo.id + ")"});
            }).catch(err => {
                console.log("Failed to load extra resources: " + err.message);
                setLoading({status: false, location: loadedInfo.name + " (" + loadedInfo.id + ")"});
            });
        }).catch(error => {
            setLoading({status: false, location: "Failed to load organisation..."});
            console.log("Failed to load: " + error.message);
        });

        organisations.availableMetrics.get(orgid).then(resp => {
            setMetricsData(_sanitize(metricsSchema, _stripEndpoints(resp.config)));
        }).catch(err => {
            console.log("Failed to load available metrics: " + err.message);
        });
    }, []);

    if (!info) return null;

    const handleOrgChange = ({data, errors}) => {
        setInfo(data);
        setEditErrors(!!errors.length);
        setOptionsDirty(true);
    };

    const handleMetricsChange = ({data, errors}) => {
        setMetricsData(data);
        setMetricsErrors(!!errors.length);
        setMetricsDirty(true);
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
            <SchemaForm
                schema={_orgSchema}
                uischema={_uiInfoSchema}
                data={info}
                readonly={!editing}
                onChange={handleOrgChange}
            />
            <SchemaForm
                schema={_orgSchema}
                uischema={_uiOptionsSchema}
                data={info}
                readonly={!editing}
                onChange={handleOrgChange}
            />
            {metricsData && (
                <SchemaForm
                    schema={metricsSchema}
                    uischema={_uiMetricsSchema}
                    data={metricsData}
                    readonly={!editing}
                    onChange={handleMetricsChange}
                />
            )}
            <ScheduledReports orgid={orgid} />
        </div>
    );
}

export {OneOrg};
